/**
 * TranslateBox — one-tap translate widget. Zero typing required:
 *   - Source = paste from clipboard, voice dictation, or "use last selection".
 *   - Target lang = a 5-button strip of common targets, smart-defaulted by:
 *       1. The user's UI locale (always offered).
 *       2. The country code (e.g. JP → ja, KR → ko, CN → zh) when traveling.
 *       3. A small phrasebook of "essentials in this country" — preset
 *          phrases like "where is the bathroom", "halal food", "thank you"
 *          that the user can tap to translate without ever typing.
 *
 * Backend chain (free + private):
 *   1. LibreTranslate self-hosted (preferred — runs on the mini PC alongside
 *      BOS at libretranslate.learnature-ly.com when configured).
 *   2. Public LibreTranslate mirror (libretranslate.com — rate-limited but
 *      free for our beta volume).
 *   3. Final fallback: open the OS clipboard with translated text via
 *      Google Translate URL — only when both APIs fail. The user stays in
 *      the app; only data leaves.
 *
 * Privacy: nothing is logged server-side; phrases are sent to the
 * translate endpoint only. No history is persisted by default (the user
 * can opt in to a 5-entry recent history, kept in localStorage only).
 */

import type { Widget, WidgetContext } from '../types';

const LANG_LABEL_KEYS: Record<string, string> = {
  en: 'lang_en',
  fr: 'lang_fr',
  ar: 'lang_ar',
  es: 'lang_es',
  de: 'lang_de',
  ja: 'lang_ja',
  ko: 'lang_ko',
  zh: 'lang_zh',
  it: 'lang_it',
  pt: 'lang_pt',
  ru: 'lang_ru',
  tr: 'lang_tr',
  nl: 'lang_nl',
  hi: 'lang_hi',
  id: 'lang_id',
};

/** Country-code → likely native language. Used to prefer local language as
 *  the target when the user is traveling; covers the top 30 countries. */
const COUNTRY_LANG: Record<string, string> = {
  US: 'en', GB: 'en', AU: 'en', CA: 'en', IE: 'en', NZ: 'en',
  FR: 'fr', BE: 'fr', LU: 'fr', SN: 'fr', CI: 'fr',
  DE: 'de', AT: 'de', CH: 'de',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es',
  IT: 'it',
  PT: 'pt', BR: 'pt',
  NL: 'nl',
  RU: 'ru', BY: 'ru',
  TR: 'tr',
  JP: 'ja',
  KR: 'ko',
  CN: 'zh', TW: 'zh', HK: 'zh',
  IN: 'hi',
  ID: 'id',
  // Arabic-speaking
  SA: 'ar', AE: 'ar', EG: 'ar', MA: 'ar', DZ: 'ar', TN: 'ar', JO: 'ar',
  QA: 'ar', KW: 'ar', BH: 'ar', OM: 'ar', LB: 'ar', SY: 'ar', LY: 'ar',
};

interface PhrasebookEntry { id: string; key: string; }

/** 8 essential travel phrases — every UI language has them via i18n keys. */
const PHRASEBOOK: PhrasebookEntry[] = [
  { id: 'hello', key: 'phrase_hello' },
  { id: 'thanks', key: 'phrase_thanks' },
  { id: 'where_bathroom', key: 'phrase_where_bathroom' },
  { id: 'halal_food', key: 'phrase_halal_food' },
  { id: 'how_much', key: 'phrase_how_much' },
  { id: 'help', key: 'phrase_help' },
  { id: 'no_pork', key: 'phrase_no_pork' },
  { id: 'pharmacy', key: 'phrase_pharmacy' },
];

const LIBRE_ENDPOINTS = [
  '/aweni/api/v1/translate', // backend proxy (preferred, no CORS, no leaks)
  'https://libretranslate.com/translate', // public fallback
];

interface TranslateResult {
  translated: string;
  detectedSource?: string;
  endpoint: string;
}

async function translate(text: string, source: string, target: string): Promise<TranslateResult | null> {
  for (const endpoint of LIBRE_ENDPOINTS) {
    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: text, source, target, format: 'text' }),
        cache: 'no-store',
      });
      if (!resp.ok) continue;
      const data = await resp.json() as { translatedText?: string; detectedLanguage?: { language?: string } };
      if (data?.translatedText) {
        return {
          translated: data.translatedText,
          detectedSource: data.detectedLanguage?.language,
          endpoint,
        };
      }
    } catch { /* try next */ }
  }
  return null;
}

export const TranslateBox: Widget = {
  id: 'translate-box',
  label: {
    en: 'Translate',
    fr: 'Traduire',
    ar: 'ترجمة',
    es: 'Traducir',
    de: 'Übersetzen',
  },
  icon: '🌐',
  defaultVisible: true,
  defaultOrder: 14,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const userLang = (ctx.locale || 'en').split('-')[0];
    const countryLang = loc.countryCode ? COUNTRY_LANG[loc.countryCode] : null;

    // Smart target language menu: country lang first (if traveling-relevant
    // and not the same as UI), then UI lang, then 3 popular fallbacks the
    // user is likely to need. Dedup so the user never sees a duplicate.
    const targets: string[] = [];
    if (countryLang && countryLang !== userLang) targets.push(countryLang);
    targets.push(userLang);
    for (const fallback of ['en', 'fr', 'ar', 'es']) {
      if (!targets.includes(fallback) && targets.length < 5) targets.push(fallback);
    }

    container.innerHTML = `
      <div class="aweni-translate-card" style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🌐</span>
          <span style="font-size:13px;font-weight:700;color:var(--ln-text-primary,var(--text-primary,#1E293B));">${escapeHtml(t('widget_translate_title') || 'Translate')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">${escapeHtml(t('widget_translate_no_typing') || 'No typing — paste, speak, or tap')}</span>
        </div>

        <!-- Action chips: paste / voice -->
        <div style="display:flex;gap:8px;margin-bottom:10px;flex-wrap:wrap;">
          <button type="button" data-act="paste"
            style="flex:1;min-height:44px;padding:8px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:var(--ln-bg-card,#fff);font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;justify-content:center;color:inherit;">
            📋 <span>${escapeHtml(t('widget_translate_paste') || 'Paste & translate')}</span>
          </button>
          <button type="button" data-act="voice"
            style="flex:1;min-height:44px;padding:8px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:var(--ln-bg-card,#fff);font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;justify-content:center;color:inherit;">
            🎙 <span>${escapeHtml(t('widget_translate_voice') || 'Speak')}</span>
          </button>
        </div>

        <!-- Target language strip -->
        <div style="display:flex;gap:6px;margin-bottom:10px;flex-wrap:wrap;">
          ${targets.map((lang, idx) => `
            <button type="button" data-target="${lang}" data-active="${idx === 0 ? '1' : '0'}"
              style="padding:6px 10px;border-radius:999px;border:1px solid ${idx === 0 ? 'var(--ln-accent,#0A0A0A)' : 'var(--ln-border,rgba(0,0,0,0.10))'};background:${idx === 0 ? 'var(--ln-accent,#0A0A0A)' : 'transparent'};color:${idx === 0 ? 'var(--ln-accent-text,#fff)' : 'inherit'};font-size:11px;font-weight:600;cursor:pointer;text-transform:uppercase;">
              ${escapeHtml(lang.toUpperCase())} <span style="opacity:0.7;font-weight:500;text-transform:none;">${escapeHtml(t(LANG_LABEL_KEYS[lang] || '') || '')}</span>
            </button>
          `).join('')}
        </div>

        <!-- Phrasebook (only when target ≠ user lang and user is traveling/has country mismatch) -->
        ${countryLang && countryLang !== userLang ? `
          <div style="margin-bottom:10px;">
            <div style="font-size:10px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px;">${escapeHtml(t('widget_translate_phrasebook') || 'Quick phrases')}</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap;">
              ${PHRASEBOOK.map((p) => `
                <button type="button" data-phrase="${p.id}" data-phrase-text="${escapeAttr(t(p.key) || p.id)}"
                  style="padding:6px 10px;border-radius:8px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));background:transparent;font-size:12px;cursor:pointer;color:inherit;">
                  ${escapeHtml(t(p.key) || p.id)}
                </button>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Output -->
        <div data-output style="display:none;padding:12px;border-radius:10px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));font-size:14px;line-height:1.5;color:var(--ln-text-primary,var(--text-primary,#1E293B));"></div>
        <div data-status style="margin-top:8px;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));min-height:14px;"></div>
      </div>
    `;

    let activeTarget = targets[0];

    const setStatus = (msg: string): void => {
      const el = container.querySelector('[data-status]') as HTMLElement | null;
      if (el) el.textContent = msg;
    };

    const showOutput = (text: string): void => {
      const el = container.querySelector('[data-output]') as HTMLElement | null;
      if (el) {
        el.textContent = text;
        el.style.display = 'block';
      }
    };

    const runTranslate = async (text: string): Promise<void> => {
      if (!text || !text.trim()) {
        setStatus(t('widget_translate_empty') || 'Nothing to translate.');
        return;
      }
      setStatus(t('common_loading') || 'Loading...');
      // Detect-source: pass 'auto' so LibreTranslate guesses; keeps zero typing.
      const result = await translate(text.slice(0, 500), 'auto', activeTarget);
      if (!result) {
        setStatus(t('widget_translate_offline') || 'Translate service unavailable. Try again later.');
        return;
      }
      showOutput(result.translated);
      setStatus(`${result.detectedSource?.toUpperCase() || '??'} → ${activeTarget.toUpperCase()}`);
    };

    // Wire chips
    container.querySelectorAll<HTMLButtonElement>('[data-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        activeTarget = btn.dataset.target || activeTarget;
        container.querySelectorAll<HTMLButtonElement>('[data-target]').forEach((b) => {
          const isActive = b.dataset.target === activeTarget;
          b.dataset.active = isActive ? '1' : '0';
          b.style.background = isActive ? 'var(--ln-accent,#0A0A0A)' : 'transparent';
          b.style.color = isActive ? 'var(--ln-accent-text,#fff)' : 'inherit';
          b.style.borderColor = isActive ? 'var(--ln-accent,#0A0A0A)' : 'var(--ln-border,rgba(0,0,0,0.10))';
        });
      });
    });

    // Wire paste
    const pasteBtn = container.querySelector('[data-act="paste"]') as HTMLButtonElement | null;
    pasteBtn?.addEventListener('click', async () => {
      try {
        if (!navigator.clipboard?.readText) {
          setStatus(t('widget_translate_clipboard_unavail') || 'Clipboard unavailable on this device.');
          return;
        }
        const text = await navigator.clipboard.readText();
        await runTranslate(text);
      } catch {
        setStatus(t('widget_translate_clipboard_denied') || 'Clipboard permission denied.');
      }
    });

    // Wire voice (Web Speech API — Chrome/Edge/Safari only)
    const voiceBtn = container.querySelector('[data-act="voice"]') as HTMLButtonElement | null;
    voiceBtn?.addEventListener('click', () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const W = window as any;
      const SR = W.SpeechRecognition || W.webkitSpeechRecognition;
      if (!SR) {
        setStatus(t('widget_translate_voice_unavail') || 'Voice not available on this browser.');
        return;
      }
      try {
        const rec = new SR();
        rec.lang = ctx.locale || 'en';
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        setStatus(t('widget_translate_listening') || 'Listening… speak now');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        rec.onresult = (e: any) => {
          const text = e.results?.[0]?.[0]?.transcript || '';
          runTranslate(text);
        };
        rec.onerror = () => setStatus(t('widget_translate_voice_error') || 'Voice capture failed.');
        rec.start();
      } catch {
        setStatus(t('widget_translate_voice_error') || 'Voice capture failed.');
      }
    });

    // Wire phrasebook
    container.querySelectorAll<HTMLButtonElement>('[data-phrase]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const text = btn.dataset.phraseText || '';
        runTranslate(text);
      });
    });
  },
};

// ----------------------------- helpers ---------------------------------------
function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
